import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '../i18n/i18nContext';

interface BirthdayPickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  error?: string;
  className?: string;
}

const BirthdayPicker: React.FC<BirthdayPickerProps> = ({
  value,
  onChange,
  error,
  className = '',
}) => {
  const { t } = useI18n();

  // Selection state: 'year' -> 'month' -> 'day' -> 'complete'
  const [selectionStep, setSelectionStep] = useState<'year' | 'month' | 'day' | 'complete'>('year');
  const [isOpen, setIsOpen] = useState(false);

  // Selected values
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Manual input state
  const [manualInput, setManualInput] = useState('');
  const [isManualMode, setIsManualMode] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const yearGridRef = useRef<HTMLDivElement>(null);

  // Calculate default year range
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear; // Allow all years up to current
  const minYear = currentYear - 100; // Reasonable minimum age

  // Generate years from maxYear backwards
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);

  // Month names from translations
  const months = t.dateTime.months;

  // Generate days based on selected year and month
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const days = selectedYear !== null && selectedMonth !== null
    ? Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1)
    : [];

  // Normalize ISO timestamp or date string to YYYY-MM-DD format
  const normalizeDate = useCallback((dateValue: string): string => {
    if (!dateValue) return '';
    // Handle ISO timestamps (e.g., "1983-03-05T00:00:00.000Z")
    if (dateValue.includes('T')) {
      return dateValue.split('T')[0];
    }
    return dateValue;
  }, []);

  // Format YYYY-MM-DD to dd/mm/yyyy for display
  const formatDateForDisplay = useCallback((isoDate: string): string => {
    if (!isoDate) return '';
    // Normalize ISO timestamps first
    const normalizedDate = normalizeDate(isoDate);
    const [year, month, day] = normalizedDate.split('-');
    return `${day}/${month}/${year}`;
  }, [normalizeDate]);

  // Initialize from value prop on mount and when value changes externally
  useEffect(() => {
    if (value) {
      const normalizedValue = normalizeDate(value);
      const [year, month, day] = normalizedValue.split('-').map(Number);
      if (year && month && day) {
        setSelectedYear(year);
        setSelectedMonth(month - 1); // JS months are 0-indexed
        setSelectedDay(day);
        setSelectionStep('complete');
        // Format as dd/mm/yyyy for display
        setManualInput(formatDateForDisplay(normalizedValue));
      }
    } else {
      // Reset if value is cleared
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDay(null);
      setSelectionStep('year');
      setManualInput('');
    }
  }, [value, normalizeDate, formatDateForDisplay]);

  // Parse dd/mm/yyyy to YYYY-MM-DD
  const parseDateFromDisplay = (displayDate: string): string | null => {
    const match = displayDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > getDaysInMonth(year, month - 1)) return null;
    if (year < minYear || year > maxYear) return null;

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Handle year selection
  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSelectionStep('month');
  };

  // Handle month selection
  const handleMonthSelect = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    setSelectedDay(null);
    setSelectionStep('day');
  };

  // Handle day selection
  const handleDaySelect = (day: number) => {
    setSelectedDay(day);
    setSelectionStep('complete');
    setIsOpen(false);

    if (selectedYear !== null && selectedMonth !== null) {
      const isoDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      onChange(isoDate);
      setManualInput(formatDateForDisplay(isoDate));
    }
  };

  // Handle manual input change
  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Auto-add slashes as user types
    const digitsOnly = inputValue.replace(/\D/g, '');
    if (digitsOnly.length <= 2) {
      inputValue = digitsOnly;
    } else if (digitsOnly.length <= 4) {
      inputValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    } else {
      inputValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
    }

    setManualInput(inputValue);

    // Try to parse complete date
    if (inputValue.length === 10) {
      const isoDate = parseDateFromDisplay(inputValue);
      if (isoDate) {
        const [year, month, day] = isoDate.split('-').map(Number);
        setSelectedYear(year);
        setSelectedMonth(month - 1);
        setSelectedDay(day);
        setSelectionStep('complete');
        onChange(isoDate);
      }
    }
  };

  // Handle manual input blur - validate and clear if invalid
  const handleManualInputBlur = () => {
    if (manualInput && manualInput.length > 0 && manualInput.length < 10) {
      // Incomplete input, clear it
      setManualInput('');
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDay(null);
      setSelectionStep('year');
      onChange('');
    }
  };

  // Handle clicking on the input to open picker
  const handleInputClick = () => {
    if (!isManualMode) {
      setIsOpen(true);
      if (selectionStep === 'complete') {
        setSelectionStep('year');
      }
    }
  };

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsManualMode(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-scroll to default position when year picker opens
  // Scroll 5 rows down so first visible year is 2006 (for typical adult users 30+)
  useEffect(() => {
    if (isOpen && selectionStep === 'year' && yearGridRef.current) {
      // If a year is already selected, scroll to show that year
      // Otherwise, scroll 5 rows down (first visible year will be 2006)
      const defaultScrollRows = 5; // 5 rows down = first visible year is 2006
      // Row height: button (py-2 = 8px*2 + ~20px line height = ~36px) + gap-1 (4px) = 40px
      const rowHeight = 40;

      let scrollPosition: number;

      if (selectedYear) {
        // Find the index of the selected year in the years array
        const yearIndex = years.findIndex(y => y === selectedYear);
        if (yearIndex !== -1) {
          // Calculate scroll position: 4 columns per row
          const rowIndex = Math.floor(yearIndex / 4);
          scrollPosition = rowIndex * rowHeight;
        } else {
          scrollPosition = defaultScrollRows * rowHeight;
        }
      } else {
        // Default: scroll 5 rows down
        scrollPosition = defaultScrollRows * rowHeight;
      }

      // Use setTimeout to ensure the DOM is rendered
      setTimeout(() => {
        if (yearGridRef.current) {
          yearGridRef.current.scrollTop = scrollPosition;
        }
      }, 0);
    }
  }, [isOpen, selectionStep, selectedYear, years]);

  // Reset to beginning if clicking back through steps
  const handleBackToYear = () => {
    setSelectionStep('year');
    setSelectedMonth(null);
    setSelectedDay(null);
  };

  const handleBackToMonth = () => {
    setSelectionStep('month');
    setSelectedDay(null);
  };

  // Get display value
  const getDisplayValue = (): string => {
    if (manualInput) return manualInput;
    if (value) return formatDateForDisplay(value);
    return '';
  };

  // Get step title
  const getStepTitle = (): string => {
    switch (selectionStep) {
      case 'year':
        return t.birthdayPicker?.selectYear || 'Select Year';
      case 'month':
        return t.birthdayPicker?.selectMonth || 'Select Month';
      case 'day':
        return t.birthdayPicker?.selectDay || 'Select Day';
      default:
        return '';
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          type="text"
          value={getDisplayValue()}
          onChange={handleManualInputChange}
          onBlur={handleManualInputBlur}
          onClick={handleInputClick}
          onFocus={() => setIsManualMode(true)}
          placeholder={t.birthdayPicker?.placeholder || 'dd/mm/yyyy'}
          className={`block w-full px-3 py-2 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
        />
        <button
          type="button"
          onClick={() => {
            setIsManualMode(false);
            setIsOpen(!isOpen);
            if (!isOpen && selectionStep === 'complete') {
              setSelectionStep('year');
            }
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Header with navigation breadcrumbs */}
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center text-sm">
              {selectionStep !== 'year' && selectedYear !== null && (
                <>
                  <button
                    type="button"
                    onClick={handleBackToYear}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {selectedYear}
                  </button>
                  <span className="mx-1 text-gray-400">&gt;</span>
                </>
              )}
              {selectionStep === 'day' && selectedMonth !== null && (
                <>
                  <button
                    type="button"
                    onClick={handleBackToMonth}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {months[selectedMonth]}
                  </button>
                  <span className="mx-1 text-gray-400">&gt;</span>
                </>
              )}
              <span className="text-gray-700 font-medium">{getStepTitle()}</span>
            </div>
          </div>

          {/* Selection content */}
          <div
            ref={selectionStep === 'year' ? yearGridRef : null}
            className="max-h-60 overflow-y-auto p-2"
          >
            {selectionStep === 'year' && (
              <div className="grid grid-cols-4 gap-1">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    className={`px-2 py-2 text-sm rounded hover:bg-blue-100 ${
                      selectedYear === year ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}

            {selectionStep === 'month' && (
              <div className="grid grid-cols-3 gap-1">
                {months.map((month: string, index: number) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleMonthSelect(index)}
                    className={`px-2 py-2 text-sm rounded hover:bg-blue-100 ${
                      selectedMonth === index ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-gray-700'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            )}

            {selectionStep === 'day' && (
              <div className="grid grid-cols-7 gap-1">
                {days.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDaySelect(day)}
                    className={`px-2 py-2 text-sm rounded hover:bg-blue-100 ${
                      selectedDay === day ? 'bg-blue-500 text-white hover:bg-blue-600' : 'text-gray-700'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdayPicker;
